const trimText = (value, fallback = '') =>
  typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : fallback;

export function parseMenuAddOn(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw;
  const addOnItemID = Number(
    row.addOnItemID ?? row.add_on_item_id ?? row.itemID ?? row.item_id ?? row.id,
  );
  if (!Number.isFinite(addOnItemID) || addOnItemID <= 0) return null;

  const status = row.status !== false;
  const name = trimText(
    row.name ?? row.addOnItemName ?? row.add_on_item_name ?? row.itemName,
    '',
  );
  if (!name) return null;

  const priceOverrideRaw = row.priceOverride ?? row.price_override;
  const priceOverride =
    priceOverrideRaw != null && priceOverrideRaw !== ''
      ? Number(priceOverrideRaw)
      : null;
  const priceRaw = row.price ?? priceOverride;
  const price = priceRaw != null && priceRaw !== '' ? Number(priceRaw) : NaN;
  if (!Number.isFinite(price) || price < 0) return null;

  return {
    addOnItemID,
    name,
    description: trimText(row.description, ''),
    price,
    priceOverride: Number.isFinite(priceOverride) ? priceOverride : null,
    status,
  };
}

export function parseMenuAddOnsList(list) {
  if (!Array.isArray(list)) return [];
  return list.map(parseMenuAddOn).filter(Boolean).filter((a) => a.status !== false);
}

export function buildCartMergeKey(hostId, addOns) {
  const host = Number(hostId);
  if (!Number.isFinite(host)) return '';
  const ids = (addOns || [])
    .map((a) => Number(a.id ?? a.addOnItemID))
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b);
  return ids.length ? `${host}:${ids.join(',')}` : String(host);
}

export function resolveCartLineKey(item) {
  if (item?.cartKey) return String(item.cartKey);
  return buildCartMergeKey(item.id, item.addOns);
}

/**
 * Normalize selected add-ons for a cart line.
 * Quantity is independent of host plate qty (mobile parity).
 */
export function normalizeSelectedCartAddOns(selectedAddOns) {
  return (selectedAddOns || [])
    .map((a) => {
      const id = Number(a.addOnItemID ?? a.id);
      const qty = Math.max(1, Math.round(Number(a.quantity) || 1));
      return {
        id,
        name: trimText(a.name, 'Add-on'),
        price: Number(a.price) || 0,
        quantity: qty,
      };
    })
    .filter((a) => Number.isFinite(a.id) && a.id > 0);
}

/** Normalize add-ons on a stored/hydrated cart line (default qty 1). */
export function normalizeCartLineAddOns(addOns) {
  return normalizeSelectedCartAddOns(addOns);
}

export function mergeAddOnQuantities(existingAddOns, incomingAddOns) {
  const map = new Map();
  for (const a of normalizeCartLineAddOns(existingAddOns)) {
    map.set(a.id, { ...a });
  }
  for (const a of normalizeCartLineAddOns(incomingAddOns)) {
    const prev = map.get(a.id);
    if (prev) {
      map.set(a.id, {
        ...prev,
        quantity: prev.quantity + a.quantity,
        name: a.name || prev.name,
        price: Number.isFinite(a.price) ? a.price : prev.price,
      });
    } else {
      map.set(a.id, { ...a });
    }
  }
  return [...map.values()].sort((a, b) => a.id - b.id);
}

/**
 * Apply add-ons to a host cart line.
 * `line.price` / `hostUnitPrice` stay as host unit only; use getCartLineTotal for money.
 */
export function applyAddOnsToCartLine(baseLine, selectedAddOns) {
  const addOns = normalizeSelectedCartAddOns(selectedAddOns);
  const hostUnitPrice =
    Number(
      baseLine.hostUnitPrice != null ? baseLine.hostUnitPrice : baseLine.price,
    ) || 0;
  const cartKey = buildCartMergeKey(baseLine.id, addOns);
  return {
    ...baseLine,
    cartKey,
    addOns,
    hostUnitPrice,
    price: hostUnitPrice,
    priceDisplay: `₹${hostUnitPrice.toFixed(2)}`,
  };
}

/**
 * Host-only unit price for a stored cart line.
 * Prefer `hostUnitPrice`; otherwise treat `price` as host-only.
 * Do not peel add-on units here — checkout/payment often pass host-only
 * `price` plus `addOns`, and peeling would understate dish + fees.
 */
export function getCartLineHostUnitPrice(line) {
  if (!line) return 0;
  if (line.hostUnitPrice != null && Number.isFinite(Number(line.hostUnitPrice))) {
    return Number(line.hostUnitPrice);
  }
  return Math.max(0, Number(line.price) || 0);
}

/**
 * One-time peel for legacy cart rows where `price` baked in add-on unit
 * prices (qty 1 each) and `hostUnitPrice` was never stored.
 */
function resolveLegacyHostUnitPrice(row, addOns) {
  if (row.hostUnitPrice != null && Number.isFinite(Number(row.hostUnitPrice))) {
    return Number(row.hostUnitPrice);
  }
  const raw = Math.max(0, Number(row.price) || 0);
  if (!addOns.length) return raw;
  const bakedAddOnUnits = addOns.reduce((sum, a) => sum + (Number(a.price) || 0), 0);
  if (bakedAddOnUnits > 0 && raw >= bakedAddOnUnits) {
    return Math.max(0, raw - bakedAddOnUnits);
  }
  return raw;
}

/** Add-on money for a line (independent of host qty). */
export function getCartLineAddOnTotal(line) {
  return normalizeCartLineAddOns(line?.addOns).reduce(
    (sum, a) => sum + (Number(a.price) || 0) * Math.max(1, Number(a.quantity) || 1),
    0,
  );
}

/**
 * Line total: hostUnit × hostQty + Σ (addOn.unit × addOn.qty).
 */
export function getCartLineTotal(line) {
  if (!line) return 0;
  const hostQty = Math.max(0, Number(line.quantity) || 0);
  const hostUnit = getCartLineHostUnitPrice(line);
  return hostUnit * hostQty + getCartLineAddOnTotal(line);
}

/**
 * Host qty label: "Qty 2".
 * Pass unitPrice to append unit money: "Qty 2 · ₹500.00 each".
 */
export function formatCartLineHostQtyLabel(quantity, unitPrice) {
  const qty = Math.max(1, Math.round(Number(quantity) || 1));
  const label = `Qty ${qty}`;
  if (unitPrice == null || unitPrice === '') return label;
  const unit = Number(unitPrice);
  if (!Number.isFinite(unit)) return label;
  if (qty > 1) return `${label} · ₹${unit.toFixed(2)} each`;
  return `${label} · ₹${unit.toFixed(2)}`;
}

/**
 * Cart formula label: "Masala Dosa (2 × ₹120.00)".
 */
export function formatCartLineFormulaLabel(name, quantity, unitPrice) {
  const label = trimText(name, 'Item');
  const qty = Math.max(1, Math.round(Number(quantity) || 1));
  const unit = Number(unitPrice) || 0;
  return `${label} (${qty} × ₹${unit.toFixed(2)})`;
}

/**
 * Display title with variant folded in: "Chapati (3 pc)".
 * Skips appending when the variant is already present in the name.
 */
export function formatCartLineTitle(line) {
  if (!line) return '';
  const name = trimText(line.name, 'Item');
  const variant = trimText(line.variantLabel, '');
  if (!variant) return name;
  const nameLower = name.toLowerCase();
  const variantLower = variant.toLowerCase();
  if (
    nameLower.includes(`(${variantLower})`) ||
    nameLower.includes(`· ${variantLower}`) ||
    nameLower.endsWith(variantLower)
  ) {
    return name;
  }
  return `${name} (${variant})`;
}

/** Short label for a configured cart line, e.g. "3 pcs · Butter on top". */
export function formatCartLineCustomiseLabel(line) {
  if (!line) return '';
  const parts = [];
  const variant = trimText(line.variantLabel, '');
  if (variant) parts.push(variant);
  const addOns = normalizeCartLineAddOns(line.addOns);
  if (addOns.length) {
    parts.push(
      addOns
        .map((a) => {
          const name = trimText(a.name, 'Add-on');
          const q = Math.max(1, Number(a.quantity) || 1);
          return q > 1 ? `${q}× ${name}` : name;
        })
        .filter(Boolean)
        .join(', '),
    );
  }
  return parts.join(' · ');
}

/**
 * Host + add-on rows for sheet / modal “already in cart” summary.
 * Headline is option-only (variant + optional single add-on price hint) — add-on qty lines are listed once below.
 */
export function formatCartLineSummaryParts(line) {
  if (!line) return { title: '', hostLine: '', addonLines: [] };
  const hostQty = Math.max(1, Number(line.quantity) || 1);
  const variant = trimText(line.variantLabel, '');
  const name = trimText(line.name, 'Item');
  const hostLabel = variant ? `${name} - ${variant}` : name;
  const addOns = normalizeCartLineAddOns(line.addOns);

  let headline = variant;
  if (headline && addOns.length === 1) {
    headline = `${headline} (+₹${Number(addOns[0].price || 0).toFixed(0)})`;
  }

  return {
    title: headline,
    titleWithPrice: headline,
    hostLine: `${hostQty}x ${hostLabel}`,
    addonLines: addOns.map(
      (a) => `${Math.max(1, Number(a.quantity) || 1)}x ${trimText(a.name, 'Add-on')}`,
    ),
  };
}

export function parseOrderLineAddOns(row) {
  if (!row || typeof row !== 'object') return [];
  const r = row;
  const list = r.addOns ?? r.add_ons ?? r.addons;
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry;
    const id = Number(
      e.addOnItemID ?? e.add_on_item_id ?? e.itemID ?? e.item_id ?? e.id,
    );
    const name = trimText(
      e.name ?? e.addOnItemName ?? e.add_on_item_name ?? e.itemName,
      '',
    );
    const price = Number(e.price ?? e.unitPrice ?? e.unit_price ?? 0);
    const quantity = Math.max(1, Math.round(Number(e.quantity ?? e.qty) || 1));
    if (!Number.isFinite(id) || id <= 0 || !name) continue;
    out.push({
      id,
      name,
      price: Number.isFinite(price) && price >= 0 ? price : 0,
      quantity,
    });
  }
  return out;
}

/** Normalize a cart row from localStorage / reorder. */
/**
 * Flat list of add-on item IDs for APIs that expect `addOns: number[]` (one entry per unit).
 */
export function buildAddOnIdsForApi(addOns) {
  const out = [];
  for (const a of normalizeCartLineAddOns(addOns)) {
    const id = Number(a.id);
    const qty = Math.max(1, Math.round(Number(a.quantity) || 1));
    if (!Number.isFinite(id) || id <= 0) continue;
    for (let i = 0; i < qty; i += 1) out.push(id);
  }
  return out;
}

export function normalizeCartLineRow(row) {
  if (!row || typeof row !== 'object') return null;
  const addOns = normalizeCartLineAddOns(row.addOns);
  const hostUnitPrice = resolveLegacyHostUnitPrice(row, addOns);
  const quantity = Math.max(1, Math.round(Number(row.quantity) || 1));
  const cartKey = resolveCartLineKey({ ...row, addOns });
  return {
    ...row,
    cartKey,
    addOns,
    hostUnitPrice,
    price: hostUnitPrice,
    priceDisplay:
      row.priceDisplay && String(row.priceDisplay).trim()
        ? row.priceDisplay
        : `₹${hostUnitPrice.toFixed(2)}`,
    quantity,
    addedAt: Number(row.addedAt) || 0,
  };
}
